<script setup lang="ts">
/**
 * Fair Home redesign shell — Track H
 * Figma RedisignHomePage · nodes `15:*`
 * H11 YSL pager: our own (`useFairHomeV2OwnPager`) — Swiper lost a quick next swipe
 * made during its transition
 * H10 Persona ✅ · H13 Search drawer ✅
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { PROFILE_ROUTE, STYLE_SPACE_ROUTE } from '@/router/routes'
import { useAudience } from '@/composables/useAudience'
import { useAuthModal, useTokenStore } from '@/features/auth/store'
import { useFairBottomNav } from '@/features/bottom-nav/store/useFairBottomNavStore'
import { useFairPageLoaderReady } from '@/features/page-loader/useFairPageLoaderReady'
import { FairSiteHeader } from '@/ui/patterns/FairSiteHeader'
import {
  resolveFairSiteHeaderSize,
  type FairSiteHeaderAction,
} from '@/ui/tokens/fair-site-header.data'
import FairHomeV2AiBlock from './FairHomeV2AiBlock.vue'
import FairHomeV2Capsules from './FairHomeV2Capsules.vue'
import FairHomeV2Hero from './FairHomeV2Hero.vue'
import FairHomeV2Selected from './FairHomeV2Selected.vue'
import { FairFooter } from '@/ui/patterns/FairFooter'
import FairHomeV2Popular from './FairHomeV2Popular.vue'
import FairHomeV2MenuDrawer from './FairHomeV2MenuDrawer.vue'
import FairHomeV2SearchDrawer from './FairHomeV2SearchDrawer.vue'
import FairHomeV2Services from './FairHomeV2Services.vue'
import {
  FAIR_HOME_V2_DESKTOP_MIN_WIDTH,
  FAIR_HOME_V2_SECTIONS,
  isFairHomeV2Compact,
} from './fair-home-v2.data'
import { FAIR_HOME_V2_PAGER_SPEED_MS } from './fair-home-v2-scroll.data'
import { useFairHomeV2LogoFlight } from './useFairHomeV2LogoFlight'
import { useFairHomeV2Persona } from './useFairHomeV2Persona'
import { useFairHomeV2Scroll } from './useFairHomeV2Scroll'
import { useFairHomeV2SlideFit } from './useFairHomeV2SlideFit'
import { useFairHomeV2OwnPager } from './useFairHomeV2OwnPager'
import fairLogoWordmarkUrl from '@/ui/icons/brand/fair-logo-wordmark.svg'
import './fair-home-v2.css'

const viewportWidth = ref(
  typeof window !== 'undefined' ? window.innerWidth : FAIR_HOME_V2_DESKTOP_MIN_WIDTH,
)

const isCompact = computed(() => isFairHomeV2Compact(viewportWidth.value))
const headerSize = computed(() => resolveFairSiteHeaderSize(viewportWidth.value))

// Was `ref('women')` — detached from the URL, so a `?audience=man` link opened
// on womenswear and the choice died on every remount.
const audience = useAudience()
const router = useRouter()
const searchOpen = ref(false)
const tokenStore = useTokenStore()
const authModal = useAuthModal()
const menuOpen = ref(false)
const activeSlide = ref(0)
const pagerRef = ref<HTMLElement | null>(null)

const { persona } = useFairHomeV2Persona()
const { reducedMotion } = useFairHomeV2Scroll()

/**
 * Hold the branded loader until the hero rail is fully dressed. It is the heaviest thing
 * on the first screen (~600 KB of WebP fetched by a runtime that lives outside the bundle),
 * and a half-loaded rack — bare rail, garments popping in one by one — is exactly what the
 * overlay exists to hide. Only one FairHomeV2Hero is mounted at a time (compact/desktop
 * branch), so a single flag is enough. If the rail never reports in, the store's
 * FAIR_PAGE_LOADER_MAX_MS cap dismisses the overlay anyway.
 */
const heroRailLoaded = ref(false)

function handleHeroRailLoad() {
  heroRailLoaded.value = true
}

useFairPageLoaderReady(heroRailLoaded)

/**
 * FE-002 — the wordmark belongs to the hero on the first screen and to the
 * header from the second one on, exactly as the mock states it (header `logo`
 * hidden on `11:42055`, visible below). Crossing that boundary plays the
 * flight; the pager's own speed keeps the two motions in step.
 */
const rootRef = ref<HTMLElement | null>(null)
const {
  headerLogoHidden,
  heroWordmarkHidden,
  flightRef: logoFlightRef,
  flightVisible: logoFlightVisible,
  play: playLogoFlight,
  settle: settleLogoFlight,
  stop: stopLogoFlight,
} = useFairHomeV2LogoFlight(rootRef, FAIR_HOME_V2_PAGER_SPEED_MS)

/**
 * Laptop heights: sections drawn for 900px are zoomed to fit instead of scrolling.
 * T-019 — phones too: a section taller than the band between the header and the bottom nav
 * is zoomed down to fit it, so nothing is cut.
 */
useFairHomeV2SlideFit(
  rootRef,
  computed(() => !reducedMotion.value),
)

function onResize() {
  viewportWidth.value = window.innerWidth
}

onMounted(() => {
  window.addEventListener('resize', onResize, { passive: true })
  // Without the pager there is no slide boundary to cross, so the header keeps
  // the wordmark from the start rather than being stuck with an empty slot.
  if (reducedMotion.value) settleLogoFlight('header')
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
  stopLogoFlight()
})

function handleHeaderAction(action: FairSiteHeaderAction) {
  if (action.id === 'account') {
    if (tokenStore.isAuthenticated()) {
      void router.push(PROFILE_ROUTE)
    } else {
      authModal.showAuthModal('signInSignUpWrapper')
    }
  } else if (action.id === 'search') {
    searchOpen.value = true
  } else if (action.id === 'style-space') {
    void router.push(STYLE_SPACE_ROUTE)
  }
}

function handleMenuClick() {
  menuOpen.value = true
}

const bottomNav = useFairBottomNav()

function handleSlideChange(index: number, previous: number) {
  activeSlide.value = index

  // T-019 — the pager has no window scroll, so it drives the bottom bar the way scrolling does on
  // other pages: paging down hides the bar (and the AIRA ball docked in it — it covered the
  // sections' bottom buttons), paging up brings it back.
  if (index > previous) bottomNav.setHidden(true)
  else if (index < previous) bottomNav.setHidden(false)

  const wasHero = previous === 0
  const isHero = index === 0
  if (wasHero !== isHero) playLogoFlight(isHero ? 'hero' : 'header')
}

useFairHomeV2OwnPager(pagerRef, {
  count: FAIR_HOME_V2_SECTIONS.length,
  speedMs: FAIR_HOME_V2_PAGER_SPEED_MS,
  onChange: handleSlideChange,
})

// T-014 — the pager has no window scroll: while the footer slide is active it holds the
// bottom nav away itself, and lets it back when the shopper pages up.
const footerActive = computed(() => FAIR_HOME_V2_SECTIONS[activeSlide.value]?.id === 'footer')
watch(footerActive, (active) => {
  if (active) bottomNav.claim('home-footer')
  else bottomNav.release('home-footer')
})
onBeforeUnmount(() => bottomNav.release('home-footer'))

/** Footer slide is dark edge-to-edge — the fixed header would sit on top of
 *  its link columns, so it fades out while the footer is active. */
const headerHidden = computed(
  () =>
    !reducedMotion.value
    && FAIR_HOME_V2_SECTIONS[activeSlide.value]?.id === 'footer',
)

function sectionFigmaNode(
  section: (typeof FAIR_HOME_V2_SECTIONS)[number],
): string {
  return isCompact.value ? section.figmaCompact : section.figmaDesktop
}

function slideClass(sectionId: string): Record<string, boolean> {
  return {
    'fair-home-v2__slide': true,
    'fair-home-v2__slide--hero': sectionId === 'hero',
    'fair-home-v2__slide--footer': sectionId === 'footer',
    'fair-home-v2__slide--services': sectionId === 'services',
  }
}

function isHeroSection(sectionId: string): boolean {
  return sectionId === 'hero'
}

function isCapsulesSection(sectionId: string): boolean {
  return sectionId === 'capsules'
}

function isServicesSection(sectionId: string): boolean {
  return sectionId === 'services'
}

function isSelectedSection(sectionId: string): boolean {
  return sectionId === 'selected'
}

function isAiSection(sectionId: string): boolean {
  return sectionId === 'ai'
}

function isPopularSection(sectionId: string): boolean {
  return sectionId === 'popular'
}

function isFooterSection(sectionId: string): boolean {
  return sectionId === 'footer'
}
</script>

<template>
  <div
    ref="rootRef"
    class="fair-home-v2"
    :class="{
      'fair-home-v2--compact': isCompact,
      'fair-home-v2--pager': !reducedMotion,
      'fair-home-v2--static': reducedMotion,
    }"
    data-unit="H11"
    data-scroll="own-pager"
  >
    <FairSiteHeader
      v-model:audience="audience"
      class="fair-home-v2__header"
      :class="{ 'fair-home-v2__header--hidden': headerHidden }"
      :aria-hidden="headerHidden ? 'true' : undefined"
      :inert="headerHidden || undefined"
      :size="headerSize"
      :logo-hidden="headerLogoHidden"
      @menu-click="handleMenuClick"
      @action-click="handleHeaderAction"
    />

    <!-- FE-002 — the wordmark in transit between the hero and the header. Fixed,
         so it is unaffected by the slide translating underneath it. -->
    <img
      ref="logoFlightRef"
      class="fair-home-v2__logo-flight"
      :class="{ 'fair-home-v2__logo-flight--visible': logoFlightVisible }"
      :src="fairLogoWordmarkUrl"
      alt=""
      aria-hidden="true"
    >

    <!-- prefers-reduced-motion: normal document stack, no wheel pager -->
    <main
      v-if="reducedMotion"
      class="fair-home-v2__main fair-home-v2__main--static"
    >
      <template
        v-for="section in FAIR_HOME_V2_SECTIONS"
        :key="section.id"
      >
        <FairHomeV2Hero
          v-if="isHeroSection(section.id)"
          :compact="isCompact"
          :wordmark-hidden="heroWordmarkHidden"
          :data-unit="section.unit"
          :data-figma="sectionFigmaNode(section)"
          @rail-load="handleHeroRailLoad"
        />
        <FairHomeV2Capsules
          v-else-if="isCapsulesSection(section.id)"
          :compact="isCompact"
          :audience="audience"
          :viewport-width="viewportWidth"
          :data-unit="section.unit"
          :data-figma="sectionFigmaNode(section)"
        />
        <FairHomeV2Services
          v-else-if="isServicesSection(section.id)"
          :compact="isCompact"
          :data-unit="section.unit"
          :data-figma="sectionFigmaNode(section)"
        />
        <FairHomeV2Selected
          v-else-if="isSelectedSection(section.id)"
          :compact="isCompact"
          :audience="audience"
          :viewport-width="viewportWidth"
          :persona="persona.selected"
          :data-unit="section.unit"
          :data-figma="sectionFigmaNode(section)"
        />
        <FairHomeV2AiBlock
          v-else-if="isAiSection(section.id)"
          :compact="isCompact"
          :persona="persona.ai"
          :completeness-percent="persona.score"
          :data-unit="section.unit"
          :data-figma="sectionFigmaNode(section)"
        />
        <FairHomeV2Popular
          v-else-if="isPopularSection(section.id)"
          :compact="isCompact"
          :audience="audience"
          :data-unit="section.unit"
          :data-figma="sectionFigmaNode(section)"
        />
        <FairFooter
          v-else-if="isFooterSection(section.id)"
          :compact="isCompact"
          :data-unit="section.unit"
          :data-figma="sectionFigmaNode(section)"
        />
      </template>
    </main>

    <!-- YSL parity: the next section slides up over the current one -->
    <div
      v-else
      ref="pagerRef"
      class="fair-home-v2__pager"
      role="region"
      aria-label="Home sections"
    >
      <section
        v-for="section in FAIR_HOME_V2_SECTIONS"
        :key="section.id"
        :class="slideClass(section.id)"
        :data-section="section.id"
        :data-unit="section.unit"
        :data-figma="sectionFigmaNode(section)"
      >
        <div class="fair-home-v2__slide-inner">
          <FairHomeV2Hero
            v-if="isHeroSection(section.id)"
            :compact="isCompact"
            :wordmark-hidden="heroWordmarkHidden"
            @rail-load="handleHeroRailLoad"
          />
          <FairHomeV2Capsules
            v-else-if="isCapsulesSection(section.id)"
            :compact="isCompact"
            :audience="audience"
            :viewport-width="viewportWidth"
          />
          <FairHomeV2Services
            v-else-if="isServicesSection(section.id)"
            :compact="isCompact"
          />
          <FairHomeV2Selected
            v-else-if="isSelectedSection(section.id)"
            :compact="isCompact"
            :audience="audience"
            :viewport-width="viewportWidth"
            :persona="persona.selected"
          />
          <FairHomeV2AiBlock
            v-else-if="isAiSection(section.id)"
            :compact="isCompact"
            :persona="persona.ai"
            :completeness-percent="persona.score"
          />
          <FairHomeV2Popular
            v-else-if="isPopularSection(section.id)"
            :compact="isCompact"
            :audience="audience"
          />
          <FairFooter
            v-else-if="isFooterSection(section.id)"
            :compact="isCompact"
          />
        </div>
      </section>
    </div>

    <FairHomeV2SearchDrawer
      v-model:open="searchOpen"
      :compact="isCompact"
      :audience="audience"
    />

    <FairHomeV2MenuDrawer
      v-model:open="menuOpen"
      v-model:audience="audience"
      :compact="isCompact"
    />
  </div>
</template>
